import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';

const PayoutProcessGuide = () => {
  const [activeTab, setActiveTab] = useState('timeline');

  const samplePayoutHistory = [
    {
      period: 'November 2025',
      iapRevenue: 3240,
      adRevenue: 1580,
      totalRevenue: 4820,
      platformFee: 964,
      payout: 3856,
      status: 'completed',
      payoutDate: 'Dec 8, 2025',
    },
    {
      period: 'December 2025',
      iapRevenue: 4120,
      adRevenue: 1890,
      totalRevenue: 6010,
      platformFee: 1202,
      payout: 4808,
      status: 'processing',
      payoutDate: 'Jan 8, 2026',
    },
    {
      period: 'January 2026',
      iapRevenue: 2950,
      adRevenue: 1420,
      totalRevenue: 4370,
      platformFee: 874,
      payout: 3496,
      status: 'pending',
      payoutDate: 'Feb 8, 2026',
    },
  ];

  const sampleRevenuBreakdown = {
    grossRevenue: 10000,
    platformFee: 2000,
    netPayout: 8000,
    iapRevenue: 6500,
    adRevenue: 3500,
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Payout Process Guide - How You Get Paid | Solis Games</title>
        <meta
          name="description"
          content="Complete guide to Solis Games payout process. Automated monthly payouts via Stripe Connect with 20% platform fee. Set up bank account, track revenue, get paid."
        />
      </Helmet>

      <Header />

      <main className="pt-16 pb-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-emerald-500/5 via-green-500/5 to-background py-20">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <span className="mb-4 bg-green-500/10 text-green-600 border-green-500/20">
                PAYMENT GUIDE
              </span>
              <h1 className="text-5xl md:text-6xl font-black mb-6 bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">
                How You Get Paid
              </h1>
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                Automated monthly payouts via Stripe Connect. Keep 80% of all revenue (IAP + ads).
                No hidden fees. No minimum thresholds.
              </p>

              <div className="flex flex-wrap items-center justify-center gap-4 mb-8">
                <div className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg">
                  <Icon name="Calendar" size={20} className="text-green-600" />
                  <span className="text-sm font-semibold">Monthly Payouts</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg">
                  <Icon name="Zap" size={20} className="text-green-600" />
                  <span className="text-sm font-semibold">Automated</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg">
                  <Icon name="Shield" size={20} className="text-green-600" />
                  <span className="text-sm font-semibold">Secure via Stripe</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg">
                  <Icon name="DollarSign" size={20} className="text-green-600" />
                  <span className="text-sm font-semibold">$50 Minimum</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-4 justify-center">
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 transition-opacity"
                >
                  Get Started Free
                  <Icon name="ArrowRight" size={20} />
                </Link>
                <a
                  href="#setup"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-card border border-border text-foreground rounded-lg font-semibold hover:bg-muted transition-colors"
                >
                  Set Up Payouts
                  <Icon name="Settings" size={20} />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Revenue Split Visualization */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-4 text-center">Revenue Split</h2>
              <p className="text-center text-muted-foreground mb-12">
                Simple, transparent pricing. 20% platform fee on all revenue.
              </p>

              <div className="p-8">
                <div className="text-center mb-8">
                  <div className="text-sm text-muted-foreground mb-2">Example: You earn</div>
                  <div className="text-5xl font-black text-primary mb-6">$10,000</div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className="p-6 bg-green-500/10 border-2 border-green-500/20 rounded-xl">
                    <div className="text-center">
                      <div className="text-4xl font-black text-green-600 mb-2">$8,000</div>
                      <div className="text-lg font-semibold text-green-600 mb-1">You Keep (80%)</div>
                      <div className="text-sm text-muted-foreground">Deposited to your bank account</div>
                    </div>
                  </div>

                  <div className="p-6 bg-muted border border-border rounded-xl">
                    <div className="text-center">
                      <div className="text-4xl font-black text-muted-foreground mb-2">$2,000</div>
                      <div className="text-lg font-semibold text-muted-foreground mb-1">Platform Fee (20%)</div>
                      <div className="text-sm text-muted-foreground">Covers infrastructure & payment processing</div>
                    </div>
                  </div>
                </div>

                <div className="p-6 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Icon name="Info" size={20} className="text-blue-600 mt-1" />
                    <div>
                      <h4 className="font-semibold text-blue-600 mb-2">Unified Fee Structure</h4>
                      <p className="text-sm text-muted-foreground">
                        The 20% fee applies to <strong>both</strong> in-app purchases and ad revenue. No hidden fees,
                        no transaction charges, no payment processing fees on top.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Payout Timeline */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-4">Payout Timeline</h2>
              <p className="text-muted-foreground mb-8">
                How and when you receive payments for your game revenue.
              </p>

              <div className="space-y-4">
                {[
                  {
                    day: '1st-31st',
                    title: 'Revenue Accrues',
                    desc: 'Your game generates revenue from IAP and ads throughout the month',
                    icon: 'TrendingUp',
                    color: 'blue',
                  },
                  {
                    day: '1st',
                    title: 'Month Closes',
                    desc: 'On the 1st of the following month, revenue is calculated and locked for payout',
                    icon: 'Calendar',
                    color: 'purple',
                  },
                  {
                    day: '2nd-7th',
                    title: 'Payout Processed',
                    desc: 'Stripe Connect initiates transfer to your bank account',
                    icon: 'Send',
                    color: 'yellow',
                  },
                  {
                    day: '8th-10th',
                    title: 'Funds Arrive',
                    desc: 'Money appears in your bank account (timing varies by bank)',
                    icon: 'CheckCircle2',
                    color: 'green',
                  },
                ].map((item, idx) => (
                  <div key={idx} className="p-6">
                    <div className="flex items-start gap-4">
                      <div
                        className={`w-16 h-16 bg-${item.color}-500/10 rounded-lg flex items-center justify-center flex-shrink-0`}
                      >
                        <Icon name={item.icon} size={24} className={`text-${item.color}-600`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`bg-${item.color}-500/10 text-${item.color}-600 border-${item.color}-500/20`}>
                            {item.day}
                          </span>
                          <h3 className="font-semibold text-lg">{item.title}</h3>
                        </div>
                        <p className="text-sm text-muted-foreground">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-6 bg-card border border-border rounded-lg">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Icon name="Calendar" size={20} className="text-primary" />
                  Example Timeline
                </h4>
                <div className="text-sm text-muted-foreground space-y-2">
                  <p>
                    <strong>December 1-31, 2025:</strong> You earn $5,000 in revenue (IAP + ads)
                  </p>
                  <p>
                    <strong>January 1, 2026:</strong> Revenue period closes, payout calculated ($4,000 to you after 20%
                    fee)
                  </p>
                  <p>
                    <strong>January 2-7, 2026:</strong> Stripe Connect processes the transfer
                  </p>
                  <p>
                    <strong>January 8-10, 2026:</strong> $4,000 arrives in your bank account
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Setting Up Payouts */}
        <section id="setup" className="py-16 bg-muted/30">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-4">Setting Up Your Payment Account</h2>
              <p className="text-muted-foreground mb-8">
                Connect your bank account via Stripe Connect to receive automated payouts. One-time setup, takes 2-3
                minutes.
              </p>

              <div className="space-y-6">
                {/* Step 1 */}
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                      1
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-3">Navigate to Revenue Settings</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        From your studio dashboard, go to{' '}
                        <code className="px-2 py-1 bg-muted rounded font-mono">Studio → Revenue → Setup</code>
                      </p>
                      <div className="p-4 bg-muted rounded-lg border border-border">
                        <img
                          src="/assets/docs/revenue-setup-location.png"
                          alt="Revenue setup location"
                          className="w-full rounded border border-border"
                          style={{ maxHeight: '200px', objectFit: 'cover' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                      2
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-3">Connect with Stripe</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Click "Connect Bank Account" to start Stripe onboarding. You'll be redirected to Stripe's secure
                        platform.
                      </p>
                      <div className="space-y-3">
                        <div className="flex items-start gap-3">
                          <Icon name="CheckCircle2" size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-semibold">Provide Business Information</p>
                            <p className="text-xs text-muted-foreground">
                              Studio name, address, tax ID (if applicable)
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <Icon name="CheckCircle2" size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-semibold">Connect Bank Account</p>
                            <p className="text-xs text-muted-foreground">
                              Routing number, account number, or use instant bank verification
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <Icon name="CheckCircle2" size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-semibold">Verify Identity</p>
                            <p className="text-xs text-muted-foreground">
                              Upload ID for compliance (required for payouts over $600/year)
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                      3
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-3">Complete & Get Paid</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Once verified, your account is active and ready to receive payouts. You'll see a confirmation on
                        your dashboard.
                      </p>
                      <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Icon name="CheckCircle2" size={24} className="text-green-600" />
                          <div>
                            <p className="font-semibold text-green-600">Payment Account Active</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Ready to receive monthly automated payouts
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-6 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <Icon name="Lock" size={20} className="text-blue-600 mt-1" />
                  <div>
                    <h4 className="font-semibold text-blue-600 mb-2">Security & Privacy</h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      Your banking information is handled entirely by Stripe, a PCI-compliant payment processor trusted by
                      millions of businesses worldwide. Solis Games never stores or sees your bank account details.
                    </p>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• 256-bit SSL encryption</li>
                      <li>• SOC 2 Type II certified</li>
                      <li>• Bank-level security</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Payout Dashboard Preview */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-4">Tracking Your Payouts</h2>
              <p className="text-muted-foreground mb-8">
                View your payout history, revenue breakdown, and upcoming payments in your studio dashboard.
              </p>

              {/* Tab Navigation */}
              <div className="flex gap-2 mb-6 border-b border-border">
                <button
                  onClick={() => setActiveTab('timeline')}
                  className={`px-4 py-2 font-semibold transition-colors border-b-2 ${
                    activeTab === 'timeline'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Payout History
                </button>
                <button
                  onClick={() => setActiveTab('breakdown')}
                  className={`px-4 py-2 font-semibold transition-colors border-b-2 ${
                    activeTab === 'breakdown'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Revenue Breakdown
                </button>
              </div>

              {/* Payout History Tab */}
              {activeTab === 'timeline' && (
                <div className="overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                            Period
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase">
                            Total Revenue
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase">
                            Your Payout
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                            Payout Date
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {samplePayoutHistory.map((payout, idx) => (
                          <tr key={idx} className="hover:bg-muted/50">
                            <td className="px-6 py-4 text-sm font-medium">{payout.period}</td>
                            <td className="px-6 py-4 text-sm text-right text-muted-foreground">
                              ${payout.totalRevenue.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 text-right">
                              <span className="text-lg font-bold text-green-600">
                                ${payout.payout.toLocaleString()}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-center">
                              {payout.status === 'completed' && (
                                <span className="bg-green-500/10 text-green-600 border-green-500/20">
                                  <Icon name="CheckCircle2" size={14} className="mr-1" />
                                  Paid
                                </span>
                              )}
                              {payout.status === 'processing' && (
                                <span className="bg-blue-500/10 text-blue-600 border-blue-500/20">
                                  <Icon name="Clock" size={14} className="mr-1" />
                                  Processing
                                </span>
                              )}
                              {payout.status === 'pending' && (
                                <span className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">
                                  <Icon name="AlertCircle" size={14} className="mr-1" />
                                  Pending
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 text-sm text-muted-foreground">{payout.payoutDate}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Revenue Breakdown Tab */}
              {activeTab === 'breakdown' && (
                <div className="p-8">
                  <div className="grid md:grid-cols-2 gap-6 mb-8">
                    <div className="p-6 bg-primary/5 border border-primary/10 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-muted-foreground">In-App Purchases</span>
                        <Icon name="ShoppingCart" size={20} className="text-primary" />
                      </div>
                      <div className="text-3xl font-bold text-primary">
                        ${sampleRevenuBreakdown.iapRevenue.toLocaleString()}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {((sampleRevenuBreakdown.iapRevenue / sampleRevenuBreakdown.grossRevenue) * 100).toFixed(0)}% of
                        total
                      </div>
                    </div>

                    <div className="p-6 bg-purple-500/5 border border-purple-500/10 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-muted-foreground">Ad Revenue</span>
                        <Icon name="Play" size={20} className="text-purple-600" />
                      </div>
                      <div className="text-3xl font-bold text-purple-600">
                        ${sampleRevenuBreakdown.adRevenue.toLocaleString()}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {((sampleRevenuBreakdown.adRevenue / sampleRevenuBreakdown.grossRevenue) * 100).toFixed(0)}% of
                        total
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                      <span className="font-semibold">Gross Revenue</span>
                      <span className="text-xl font-bold">
                        ${sampleRevenuBreakdown.grossRevenue.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                      <span className="font-semibold text-muted-foreground">Platform Fee (20%)</span>
                      <span className="text-xl font-bold text-muted-foreground">
                        -${sampleRevenuBreakdown.platformFee.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-green-500/10 border-2 border-green-500/20 rounded-lg">
                      <span className="font-semibold text-green-600">Your Payout</span>
                      <span className="text-2xl font-black text-green-600">
                        ${sampleRevenuBreakdown.netPayout.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <p className="text-sm text-muted-foreground mt-4 text-center">
                Example data. Your actual dashboard shows real-time revenue tracking.
              </p>
            </div>
          </div>
        </section>

        {/* Payout Requirements */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-8">Payout Requirements</h2>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-6">
                  <div className="flex items-start gap-3 mb-3">
                    <Icon name="DollarSign" size={20} className="text-primary mt-1" />
                    <h3 className="font-semibold text-lg">Minimum Payout</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    You must earn at least <strong>$50</strong> in a given month to receive a payout. Revenue below this
                    threshold rolls over to the next month.
                  </p>
                  <div className="p-3 bg-muted rounded-lg text-xs text-muted-foreground">
                    Example: Earn $30 in January → rolls over → Earn $40 in February → $70 total payout in early March
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-start gap-3 mb-3">
                    <Icon name="CheckCircle2" size={20} className="text-primary mt-1" />
                    <h3 className="font-semibold text-lg">Account Verification</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    Your Stripe Connect account must be fully verified to receive payouts. This includes:
                  </p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Bank account connected</li>
                    <li>• Identity verification (for $600+/year)</li>
                    <li>• Business information confirmed</li>
                  </ul>
                </div>

                <div className="p-6">
                  <div className="flex items-start gap-3 mb-3">
                    <Icon name="Globe" size={20} className="text-primary mt-1" />
                    <h3 className="font-semibold text-lg">Supported Countries</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    Stripe Connect supports payouts in 40+ countries. Check if your country is supported during
                    onboarding.
                  </p>
                  <a
                    href="https://stripe.com/global"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline flex items-center gap-1"
                  >
                    View Supported Countries
                    <Icon name="ExternalLink" size={14} />
                  </a>
                </div>

                <div className="p-6">
                  <div className="flex items-start gap-3 mb-3">
                    <Icon name="FileText" size={20} className="text-primary mt-1" />
                    <h3 className="font-semibold text-lg">Tax Documents</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    For US developers earning $600+/year, Stripe automatically generates 1099 tax forms. International
                    developers receive detailed transaction reports.
                  </p>
                  <p className="text-xs text-muted-foreground">Available in your Stripe Connect dashboard</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-8">Frequently Asked Questions</h2>

              <div className="space-y-4">
                {[
                  {
                    q: 'When do I receive my first payout?',
                    a: 'If you earn $50+ in your first month, you\'ll receive your first payout approximately 8-10 days into the following month. For example, revenue earned in January is paid out around February 8-10.',
                  },
                  {
                    q: 'What if I don\'t reach $50 in a month?',
                    a: 'Revenue below $50 rolls over to the next month. Once your cumulative unpaid balance reaches $50, it will be included in the next payout cycle.',
                  },
                  {
                    q: 'Are there any fees for receiving payouts?',
                    a: 'No. The 20% platform fee already covers all payment processing and payout costs. There are no additional fees charged for transfers to your bank account.',
                  },
                  {
                    q: 'Can I change my payout schedule?',
                    a: 'Currently, all studios receive monthly payouts on the same schedule. Custom payout schedules (weekly, biweekly) may be available for high-volume studios - contact support.',
                  },
                  {
                    q: 'What happens if a player requests a refund?',
                    a: 'Refunded transactions are automatically deducted from your next payout. If a large refund causes a negative balance, it will be recovered from future earnings.',
                  },
                  {
                    q: 'Can I have multiple games on one account?',
                    a: 'Yes! All games under your studio account share the same payout settings. Your dashboard shows per-game revenue breakdown, but payouts are combined.',
                  },
                  {
                    q: 'How do I update my bank account?',
                    a: 'Log into your Stripe Connect dashboard (linked from your Solis Games revenue page) to update banking information. Changes may require re-verification.',
                  },
                  {
                    q: 'What if my payout fails?',
                    a: 'Failed payouts (wrong account number, closed account) will show in your dashboard. Update your bank information in Stripe and the payout will be automatically retried.',
                  },
                ].map((item, idx) => (
                  <div key={idx} className="p-6">
                    <h3 className="font-semibold text-lg mb-3 flex items-start gap-2">
                      <Icon name="HelpCircle" size={20} className="text-primary mt-1 flex-shrink-0" />
                      {item.q}
                    </h3>
                    <p className="text-sm text-muted-foreground ml-7">{item.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-primary/10 via-green-500/10 to-background">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-4xl font-bold mb-4">Start Earning Today</h2>
              <p className="text-xl text-muted-foreground mb-8">
                Set up your payment account in minutes and start receiving automated monthly payouts.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-lg font-semibold text-lg hover:opacity-90 transition-opacity"
                >
                  Create Free Account
                  <Icon name="ArrowRight" size={20} />
                </Link>
                <Link
                  to="/developers/monetization"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-card border border-border text-foreground rounded-lg font-semibold text-lg hover:bg-muted transition-colors"
                >
                  View Revenue System
                  <Icon name="DollarSign" size={20} />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default PayoutProcessGuide;
